import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, MapPin, Clock, DollarSign, AlertCircle, TrendingUpIcon, Users, CheckCircle2 } from 'lucide-react';
type TabType = 'funnel' | 'route' | 'performance' | 'finance' | 'team';
export function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('funnel');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-7xl mx-auto" data-id="element-1265">
      {/* Header & Tabs */}
      <div className="mb-8" data-id="element-1266">
        <h2 className="text-2xl font-bold mb-6" data-id="element-1267">Analytics</h2>
        <div className="flex gap-6 border-b border-border" data-id="element-1268">
          <button onClick={() => setActiveTab('funnel')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'funnel' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-1269">
            Funnel
          </button>
          <button onClick={() => setActiveTab('route')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'route' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-1270">
            Route Economics
          </button>
          <button onClick={() => setActiveTab('performance')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'performance' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-1271">
            Performance
          </button>
          <button onClick={() => setActiveTab('finance')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'finance' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-1272">
            Finance
          </button>
          <button onClick={() => setActiveTab('team')} className={`pb-3 text-sm font-medium transition-colors ${activeTab === 'team' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-1273">
            Team / Ops
          </button>
        </div>
      </div>

      {/* Funnel Tab */}
      {activeTab === 'funnel' && <>
          <div className="flex justify-between items-center mb-6" data-id="element-1274">
            <h3 className="text-lg font-semibold" data-id="element-1275">
              Campaign Conversion Funnel
            </h3>
            <select className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-1276">
              <option data-id="element-1277">Summer Run '26</option>
              <option data-id="element-1278">All Campaigns (YTD)</option>
            </select>
          </div>

          {/* Funnel Visualization */}
          <div className="bg-surface border border-border rounded-xl p-8 mb-8" data-id="element-1279">
            <div className="flex flex-col items-center gap-2" data-id="element-1280">
              {/* Targets */}
              <div className="w-full max-w-3xl relative group" data-id="element-1281">
                <div className="h-16 bg-surfaceHover border border-border rounded-lg flex items-center justify-between px-6 transition-colors group-hover:border-primary/50" data-id="element-1282">
                  <span className="font-medium text-textMuted" data-id="element-1283">Targets</span>
                  <span className="text-2xl font-bold" data-id="element-1284">42</span>
                </div>
              </div>

              <div className="h-6 border-l-2 border-dashed border-border" data-id="element-1285"></div>

              {/* Contacted */}
              <div className="w-full max-w-2xl relative group" data-id="element-1286">
                <div className="h-16 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-between px-6 transition-colors group-hover:border-blue-500/50" data-id="element-1287">
                  <span className="font-medium text-blue-500" data-id="element-1288">Contacted</span>
                  <div className="flex items-center gap-4" data-id="element-1289">
                    <span className="text-xs text-textMuted" data-id="element-1290">
                      66% conversion
                    </span>
                    <span className="text-2xl font-bold text-blue-500" data-id="element-1291">28</span>
                  </div>
                </div>
              </div>

              <div className="h-6 border-l-2 border-dashed border-border" data-id="element-1292"></div>

              {/* Replied */}
              <div className="w-full max-w-xl relative group" data-id="element-1293">
                <div className="h-16 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center justify-between px-6 transition-colors group-hover:border-purple-500/50" data-id="element-1294">
                  <span className="font-medium text-purple-500" data-id="element-1295">Replied</span>
                  <div className="flex items-center gap-4" data-id="element-1296">
                    <span className="text-xs text-textMuted" data-id="element-1297">
                      50% conversion
                    </span>
                    <span className="text-2xl font-bold text-purple-500" data-id="element-1298">
                      14
                    </span>
                  </div>
                </div>
              </div>

              <div className="h-6 border-l-2 border-dashed border-border" data-id="element-1299"></div>

              {/* Offers */}
              <div className="w-full max-w-lg relative group" data-id="element-1300">
                <div className="h-16 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between px-6 transition-colors group-hover:border-primary/50" data-id="element-1301">
                  <span className="font-medium text-primary" data-id="element-1302">Offers</span>
                  <div className="flex items-center gap-4" data-id="element-1303">
                    <span className="text-xs text-textMuted" data-id="element-1304">
                      43% conversion
                    </span>
                    <span className="text-2xl font-bold text-primary" data-id="element-1305">6</span>
                  </div>
                </div>
              </div>

              <div className="h-6 border-l-2 border-dashed border-border" data-id="element-1306"></div>

              {/* Confirmed */}
              <div className="w-full max-w-md relative group" data-id="element-1307">
                <div className="h-16 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-between px-6 transition-colors group-hover:border-green-500/50" data-id="element-1308">
                  <span className="font-medium text-green-500" data-id="element-1309">Confirmed</span>
                  <div className="flex items-center gap-4" data-id="element-1310">
                    <span className="text-xs text-textMuted" data-id="element-1311">
                      50% conversion
                    </span>
                    <span className="text-2xl font-bold text-green-500" data-id="element-1312">3</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8 text-sm text-textMuted" data-id="element-1313">
              Overall conversion (Targets to Confirmed):{' '}
              <span className="font-medium text-textMain" data-id="element-1314">7.1%</span>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1315">
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1316">
              <div className="text-sm text-textMuted mb-2" data-id="element-1317">Reply Rate</div>
              <div className="flex items-end gap-3" data-id="element-1318">
                <span className="text-3xl font-bold" data-id="element-1319">50%</span>
                <span className="flex items-center text-xs font-medium text-green-500 mb-1" data-id="element-1320">
                  <TrendingUp className="w-3 h-3 mr-1" data-id="element-1321" /> +12%
                </span>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1322">
              <div className="text-sm text-textMuted mb-2" data-id="element-1323">Offer Rate</div>
              <div className="flex items-end gap-3" data-id="element-1324">
                <span className="text-3xl font-bold" data-id="element-1325">43%</span>
                <span className="flex items-center text-xs font-medium text-green-500 mb-1" data-id="element-1326">
                  <TrendingUp className="w-3 h-3 mr-1" data-id="element-1327" /> +8%
                </span>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1328">
              <div className="text-sm text-textMuted mb-2" data-id="element-1329">
                Confirmation Rate
              </div>
              <div className="flex items-end gap-3" data-id="element-1330">
                <span className="text-3xl font-bold" data-id="element-1331">50%</span>
                <span className="flex items-center text-xs font-medium text-red-500 mb-1" data-id="element-1332">
                  <TrendingDown className="w-3 h-3 mr-1" data-id="element-1333" /> -3%
                </span>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1334">
              <div className="text-sm text-textMuted mb-2" data-id="element-1335">Avg Deal Value</div>
              <div className="flex items-end gap-3" data-id="element-1336">
                <span className="text-3xl font-bold" data-id="element-1337">$1,850</span>
                <span className="flex items-center text-xs font-medium text-green-500 mb-1" data-id="element-1338">
                  <TrendingUp className="w-3 h-3 mr-1" data-id="element-1339" /> +$200
                </span>
              </div>
            </div>
          </div>
        </>}

      {/* Route Economics Tab */}
      {activeTab === 'route' && <>
          <div className="flex justify-between items-center mb-6" data-id="element-1340">
            <h3 className="text-lg font-semibold" data-id="element-1341">Route Economics</h3>
            <select className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-1342">
              <option data-id="element-1343">Summer Run '26</option>
              <option data-id="element-1344">All Campaigns (YTD)</option>
            </select>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-1345">
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1346">
              <div className="flex items-center gap-2 mb-2" data-id="element-1347">
                <MapPin className="w-4 h-4 text-primary" data-id="element-1348" />
                <div className="text-sm text-textMuted" data-id="element-1349">Total Drive Miles</div>
              </div>
              <div className="text-3xl font-bold" data-id="element-1350">1,847 mi</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1351">
              <div className="flex items-center gap-2 mb-2" data-id="element-1352">
                <Clock className="w-4 h-4 text-blue-500" data-id="element-1353" />
                <div className="text-sm text-textMuted" data-id="element-1354">
                  Avg Drive Between Stops
                </div>
              </div>
              <div className="text-3xl font-bold" data-id="element-1355">132 mi</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1356">
              <div className="flex items-center gap-2 mb-2" data-id="element-1357">
                <DollarSign className="w-4 h-4 text-amber-500" data-id="element-1358" />
                <div className="text-sm text-textMuted" data-id="element-1359">Cost Per Show</div>
              </div>
              <div className="text-3xl font-bold" data-id="element-1360">$485</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1361">
              <div className="flex items-center gap-2 mb-2" data-id="element-1362">
                <TrendingUpIcon className="w-4 h-4 text-green-500" data-id="element-1363" />
                <div className="text-sm text-textMuted" data-id="element-1364">Revenue Per Mile</div>
              </div>
              <div className="text-3xl font-bold" data-id="element-1365">$10.02</div>
            </div>
          </div>

          {/* Route Legs Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden mb-8" data-id="element-1366">
            <div className="overflow-x-auto" data-id="element-1367">
              <table className="w-full text-sm text-left" data-id="element-1368">
                <thead className="bg-surface border-b border-border text-textMuted" data-id="element-1369">
                  <tr data-id="element-1370">
                    <th className="px-6 py-4 font-medium" data-id="element-1371">Leg</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1372">From → To</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1373">Miles</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1374">
                      Drive Time
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1375">
                      Lodging
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1376">
                      Per Diem
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1377">
                      Leg Cost
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1378">
                      Show Revenue
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1379">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background" data-id="element-1380">
                  {[{
                leg: 1,
                from: 'Chicago',
                to: 'Cleveland',
                miles: 350,
                driveTime: '5h 20m',
                lodging: 180,
                perDiem: 120,
                revenue: 1500
              }, {
                leg: 2,
                from: 'Cleveland',
                to: 'Buffalo',
                miles: 190,
                driveTime: '3h 10m',
                lodging: 160,
                perDiem: 120,
                revenue: 1800
              }, {
                leg: 3,
                from: 'Buffalo',
                to: 'Burlington',
                miles: 280,
                driveTime: '4h 45m',
                lodging: 200,
                perDiem: 120,
                revenue: 2200
              }, {
                leg: 4,
                from: 'Burlington',
                to: 'Cambridge',
                miles: 220,
                driveTime: '3h 40m',
                lodging: 220,
                perDiem: 120,
                revenue: 1900
              }, {
                leg: 5,
                from: 'Cambridge',
                to: 'Portland',
                miles: 105,
                driveTime: '1h 50m',
                lodging: 140,
                perDiem: 120,
                revenue: 1600
              }].map(leg => {
                const legCost = leg.lodging + leg.perDiem + leg.miles * 0.67;
                const net = leg.revenue - legCost;
                const isUnprofitable = net < 500;
                return <tr key={leg.leg} className={`hover:bg-surface/50 transition-colors ${isUnprofitable ? 'bg-red-500/[0.02]' : ''}`} data-id="element-1381">
                        <td className="px-6 py-4 font-medium" data-id="element-1382">Leg {leg.leg}</td>
                        <td className="px-6 py-4" data-id="element-1383">
                          {leg.from} → {leg.to}
                        </td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1384">
                          {leg.miles} mi
                        </td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1385">
                          {leg.driveTime}
                        </td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1386">
                          ${leg.lodging}
                        </td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1387">
                          ${leg.perDiem}
                        </td>
                        <td className="px-6 py-4 text-right font-medium" data-id="element-1388">
                          ${legCost.toFixed(0)}
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-primary" data-id="element-1389">
                          ${leg.revenue}
                        </td>
                        <td className={`px-6 py-4 text-right font-bold ${isUnprofitable ? 'text-red-500' : 'text-green-500'}`} data-id="element-1390">
                          ${net.toFixed(0)}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Route Efficiency & Warnings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1391">
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1392">
              <h4 className="text-lg font-semibold mb-4" data-id="element-1393">
                Route Efficiency Score
              </h4>
              <div className="flex items-end gap-4 mb-6" data-id="element-1394">
                <div className="text-5xl font-bold text-primary" data-id="element-1395">78</div>
                <div className="text-2xl text-textMuted mb-2" data-id="element-1396">/ 100</div>
              </div>
              <div className="space-y-3" data-id="element-1397">
                <div data-id="element-1398">
                  <div className="flex justify-between text-sm mb-1" data-id="element-1399">
                    <span className="text-textMuted" data-id="element-1400">Drive Efficiency</span>
                    <span className="font-medium" data-id="element-1401">85/100</span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden" data-id="element-1402">
                    <div className="h-full bg-green-500" style={{
                  width: '85%'
                }} data-id="element-1403"></div>
                  </div>
                </div>
                <div data-id="element-1404">
                  <div className="flex justify-between text-sm mb-1" data-id="element-1405">
                    <span className="text-textMuted" data-id="element-1406">Cost Control</span>
                    <span className="font-medium" data-id="element-1407">72/100</span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden" data-id="element-1408">
                    <div className="h-full bg-amber-500" style={{
                  width: '72%'
                }} data-id="element-1409"></div>
                  </div>
                </div>
                <div data-id="element-1410">
                  <div className="flex justify-between text-sm mb-1" data-id="element-1411">
                    <span className="text-textMuted" data-id="element-1412">Revenue Optimization</span>
                    <span className="font-medium" data-id="element-1413">77/100</span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden" data-id="element-1414">
                    <div className="h-full bg-primary" style={{
                  width: '77%'
                }} data-id="element-1415"></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1416">
              <h4 className="text-lg font-semibold mb-4" data-id="element-1417">Route Warnings</h4>
              <div className="space-y-3" data-id="element-1418">
                <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-id="element-1419">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-1420" />
                  <div data-id="element-1421">
                    <div className="text-sm font-medium text-red-500 mb-1" data-id="element-1422">
                      Low margin on Leg 1
                    </div>
                    <div className="text-xs text-textMuted" data-id="element-1423">
                      Chicago → Cleveland net is only $265. Consider negotiating
                      higher guarantee.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-id="element-1424">
                  <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-1425" />
                  <div data-id="element-1426">
                    <div className="text-sm font-medium text-amber-500 mb-1" data-id="element-1427">
                      Long drive on Leg 3
                    </div>
                    <div className="text-xs text-textMuted" data-id="element-1428">
                      Buffalo → Burlington is 280 miles. Consider adding a rest
                      day.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Performance Tab */}
      {activeTab === 'performance' && <>
          <div className="flex justify-between items-center mb-6" data-id="element-1429">
            <h3 className="text-lg font-semibold" data-id="element-1430">
              Venue & Artist Performance
            </h3>
            <select className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-1431">
              <option data-id="element-1432">Summer Run '26</option>
              <option data-id="element-1433">All Campaigns (YTD)</option>
            </select>
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-1434">
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1435">
              <div className="text-sm text-textMuted mb-2" data-id="element-1436">
                Reply Rate Trend
              </div>
              <div className="flex items-end gap-3" data-id="element-1437">
                <span className="text-3xl font-bold" data-id="element-1438">50%</span>
                <span className="flex items-center text-xs font-medium text-green-500 mb-1" data-id="element-1439">
                  <TrendingUp className="w-3 h-3 mr-1" data-id="element-1440" /> +12%
                </span>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1441">
              <div className="text-sm text-textMuted mb-2" data-id="element-1442">
                Offer Rate Trend
              </div>
              <div className="flex items-end gap-3" data-id="element-1443">
                <span className="text-3xl font-bold" data-id="element-1444">43%</span>
                <span className="flex items-center text-xs font-medium text-green-500 mb-1" data-id="element-1445">
                  <TrendingUp className="w-3 h-3 mr-1" data-id="element-1446" /> +8%
                </span>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1447">
              <div className="text-sm text-textMuted mb-2" data-id="element-1448">
                Avg Response Time
              </div>
              <div className="text-3xl font-bold" data-id="element-1449">2.3 days</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1450">
              <div className="text-sm text-textMuted mb-2" data-id="element-1451">
                Best Converting Region
              </div>
              <div className="text-xl font-bold" data-id="element-1452">New England</div>
              <div className="text-xs text-textMuted mt-1" data-id="element-1453">68% reply rate</div>
            </div>
          </div>

          {/* Reply Rates by Venue Type */}
          <div className="bg-surface border border-border rounded-xl p-6 mb-8" data-id="element-1454">
            <h4 className="text-lg font-semibold mb-6" data-id="element-1455">
              Reply Rates by Venue Type
            </h4>
            <div className="space-y-4" data-id="element-1456">
              {[{
            type: 'Listening Rooms',
            rate: 71,
            color: 'green'
          }, {
            type: 'Clubs',
            rate: 62,
            color: 'primary'
          }, {
            type: 'Theaters',
            rate: 45,
            color: 'blue'
          }, {
            type: 'Festivals',
            rate: 38,
            color: 'amber'
          }].map(venue => <div key={venue.type} data-id="element-1457">
                  <div className="flex justify-between text-sm mb-2" data-id="element-1458">
                    <span className="font-medium" data-id="element-1459">{venue.type}</span>
                    <span className="text-textMuted" data-id="element-1460">{venue.rate}%</span>
                  </div>
                  <div className="h-8 bg-background rounded-lg overflow-hidden" data-id="element-1461">
                    <div className={`h-full bg-${venue.color}-500 flex items-center justify-end px-3 text-sm font-medium text-background`} style={{
                width: `${venue.rate}%`
              }} data-id="element-1462">
                      {venue.rate}%
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Top Performing Venues */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden mb-8" data-id="element-1463">
            <div className="px-6 py-4 border-b border-border" data-id="element-1464">
              <h4 className="text-lg font-semibold" data-id="element-1465">Top Performing Venues</h4>
            </div>
            <div className="overflow-x-auto" data-id="element-1466">
              <table className="w-full text-sm text-left" data-id="element-1467">
                <thead className="bg-surface border-b border-border text-textMuted" data-id="element-1468">
                  <tr data-id="element-1469">
                    <th className="px-6 py-4 font-medium" data-id="element-1470">Venue</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1471">City</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1472">Response Time</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1473">Outcome</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1474">
                      Deal Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background" data-id="element-1475">
                  {[{
                venue: 'Higher Ground',
                city: 'Burlington, VT',
                responseTime: '6 hours',
                outcome: 'Confirmed',
                value: 2200
              }, {
                venue: 'The Empty Bottle',
                city: 'Chicago, IL',
                responseTime: '1 day',
                outcome: 'Offer',
                value: 1500
              }, {
                venue: 'Beachland Tavern',
                city: 'Cleveland, OH',
                responseTime: '2 days',
                outcome: 'Hold',
                value: 1800
              }, {
                venue: 'The Sinclair',
                city: 'Cambridge, MA',
                responseTime: '3 days',
                outcome: 'Confirmed',
                value: 1900
              }].map((venue, i) => <tr key={i} className="hover:bg-surface/50 transition-colors" data-id="element-1476">
                      <td className="px-6 py-4 font-medium" data-id="element-1477">{venue.venue}</td>
                      <td className="px-6 py-4 text-textMuted" data-id="element-1478">{venue.city}</td>
                      <td className="px-6 py-4 text-textMuted" data-id="element-1479">
                        {venue.responseTime}
                      </td>
                      <td className="px-6 py-4" data-id="element-1480">
                        <span className={`px-2 py-1 text-xs rounded font-medium ${venue.outcome === 'Confirmed' ? 'bg-green-500/10 text-green-500' : venue.outcome === 'Offer' ? 'bg-primary/10 text-primary' : 'bg-amber-500/10 text-amber-500'}`} data-id="element-1481">
                          {venue.outcome}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-primary" data-id="element-1482">
                        ${venue.value}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Outreach Velocity */}
          <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1483">
            <h4 className="text-lg font-semibold mb-6" data-id="element-1484">
              Outreach Velocity Over Time
            </h4>
            <div className="space-y-3" data-id="element-1485">
              {[{
            week: 'Week 1',
            sent: 12,
            replied: 6
          }, {
            week: 'Week 2',
            sent: 18,
            replied: 9
          }, {
            week: 'Week 3',
            sent: 8,
            replied: 5
          }, {
            week: 'Week 4',
            sent: 15,
            replied: 8
          }].map(week => <div key={week.week} data-id="element-1486">
                  <div className="flex justify-between text-sm mb-2" data-id="element-1487">
                    <span className="font-medium" data-id="element-1488">{week.week}</span>
                    <span className="text-textMuted" data-id="element-1489">
                      {week.sent} sent • {week.replied} replied
                    </span>
                  </div>
                  <div className="flex gap-1 h-6" data-id="element-1490">
                    <div className="bg-blue-500 rounded" style={{
                width: `${week.sent / 20 * 100}%`
              }} data-id="element-1491"></div>
                    <div className="bg-green-500 rounded" style={{
                width: `${week.replied / 20 * 100}%`
              }} data-id="element-1492"></div>
                  </div>
                </div>)}
            </div>
          </div>
        </>}

      {/* Finance Tab */}
      {activeTab === 'finance' && <>
          <div className="flex justify-between items-center mb-6" data-id="element-1493">
            <h3 className="text-lg font-semibold" data-id="element-1494">Financial Overview</h3>
            <select className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-1495">
              <option data-id="element-1496">This Campaign</option>
              <option data-id="element-1497">YTD</option>
              <option data-id="element-1498">All Time</option>
            </select>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-1499">
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1500">
              <div className="text-sm text-textMuted mb-2" data-id="element-1501">
                Total Expected Revenue
              </div>
              <div className="text-3xl font-bold text-primary" data-id="element-1502">$18,500</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1503">
              <div className="text-sm text-textMuted mb-2" data-id="element-1504">
                Deposits Collected
              </div>
              <div className="text-3xl font-bold text-green-500" data-id="element-1505">$4,200</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1506">
              <div className="text-sm text-textMuted mb-2" data-id="element-1507">Outstanding</div>
              <div className="text-3xl font-bold text-amber-500" data-id="element-1508">$3,800</div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1509">
              <div className="text-sm text-textMuted mb-2" data-id="element-1510">Settled</div>
              <div className="text-3xl font-bold text-green-500" data-id="element-1511">$6,200</div>
            </div>
          </div>

          {/* Revenue by Show */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden mb-8" data-id="element-1512">
            <div className="px-6 py-4 border-b border-border" data-id="element-1513">
              <h4 className="text-lg font-semibold" data-id="element-1514">Revenue by Show</h4>
            </div>
            <div className="overflow-x-auto" data-id="element-1515">
              <table className="w-full text-sm text-left" data-id="element-1516">
                <thead className="bg-surface border-b border-border text-textMuted" data-id="element-1517">
                  <tr data-id="element-1518">
                    <th className="px-6 py-4 font-medium" data-id="element-1519">Date</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1520">Venue</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1521">
                      Guarantee
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1522">
                      Door Split Est.
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1523">
                      Merch Est.
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-1524">Total</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1525">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background" data-id="element-1526">
                  {[{
                date: 'Jun 12',
                venue: 'The Empty Bottle',
                guarantee: 1500,
                doorSplit: 800,
                merch: 400,
                status: 'Deposit'
              }, {
                date: 'Jun 14',
                venue: 'Beachland Tavern',
                guarantee: 1800,
                doorSplit: 600,
                merch: 350,
                status: 'Outstanding'
              }, {
                date: 'Jun 18',
                venue: 'Higher Ground',
                guarantee: 2200,
                doorSplit: 1200,
                merch: 500,
                status: 'Deposit'
              }, {
                date: 'Jun 21',
                venue: 'The Sinclair',
                guarantee: 1900,
                doorSplit: 900,
                merch: 450,
                status: 'Outstanding'
              }, {
                date: 'Mar 15',
                venue: "Antone's",
                guarantee: 2000,
                doorSplit: 1100,
                merch: 600,
                status: 'Settled'
              }].map((show, i) => {
                const total = show.guarantee + show.doorSplit + show.merch;
                return <tr key={i} className="hover:bg-surface/50 transition-colors" data-id="element-1527">
                        <td className="px-6 py-4 font-medium" data-id="element-1528">{show.date}</td>
                        <td className="px-6 py-4" data-id="element-1529">{show.venue}</td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1530">
                          ${show.guarantee}
                        </td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1531">
                          ${show.doorSplit}
                        </td>
                        <td className="px-6 py-4 text-right text-textMuted" data-id="element-1532">
                          ${show.merch}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-primary" data-id="element-1533">
                          ${total}
                        </td>
                        <td className="px-6 py-4" data-id="element-1534">
                          <span className={`px-2 py-1 text-xs rounded font-medium ${show.status === 'Settled' ? 'bg-green-500/10 text-green-500' : show.status === 'Deposit' ? 'bg-blue-500/10 text-blue-500' : 'bg-amber-500/10 text-amber-500'}`} data-id="element-1535">
                            {show.status}
                          </span>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Aged Receivables & Member Payouts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1536">
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1537">
              <h4 className="text-lg font-semibold mb-4" data-id="element-1538">Aged Receivables</h4>
              <div className="space-y-4" data-id="element-1539">
                <div className="flex justify-between items-center p-3 bg-background rounded-lg" data-id="element-1540">
                  <div data-id="element-1541">
                    <div className="text-sm font-medium" data-id="element-1542">0-30 days</div>
                    <div className="text-xs text-textMuted" data-id="element-1543">2 invoices</div>
                  </div>
                  <div className="text-xl font-bold" data-id="element-1544">$2,400</div>
                </div>
                <div className="flex justify-between items-center p-3 bg-amber-500/5 rounded-lg" data-id="element-1545">
                  <div data-id="element-1546">
                    <div className="text-sm font-medium text-amber-500" data-id="element-1547">
                      31-60 days
                    </div>
                    <div className="text-xs text-textMuted" data-id="element-1548">1 invoice</div>
                  </div>
                  <div className="text-xl font-bold text-amber-500" data-id="element-1549">$1,200</div>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-500/5 rounded-lg" data-id="element-1550">
                  <div data-id="element-1551">
                    <div className="text-sm font-medium text-red-500" data-id="element-1552">
                      60+ days
                    </div>
                    <div className="text-xs text-textMuted" data-id="element-1553">1 invoice</div>
                  </div>
                  <div className="text-xl font-bold text-red-500" data-id="element-1554">$200</div>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1555">
              <h4 className="text-lg font-semibold mb-4" data-id="element-1556">
                Member Payout Summary
              </h4>
              <div className="space-y-4" data-id="element-1557">
                <div className="flex justify-between items-center p-3 bg-background rounded-lg" data-id="element-1558">
                  <div className="text-sm font-medium" data-id="element-1559">
                    Total Owed to Members
                  </div>
                  <div className="text-xl font-bold text-primary" data-id="element-1560">$8,400</div>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-500/5 rounded-lg" data-id="element-1561">
                  <div className="text-sm font-medium text-green-500" data-id="element-1562">Paid</div>
                  <div className="text-xl font-bold text-green-500" data-id="element-1563">$5,200</div>
                </div>
                <div className="flex justify-between items-center p-3 bg-amber-500/5 rounded-lg" data-id="element-1564">
                  <div className="text-sm font-medium text-amber-500" data-id="element-1565">
                    Pending
                  </div>
                  <div className="text-xl font-bold text-amber-500" data-id="element-1566">$3,200</div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Team / Ops Tab */}
      {activeTab === 'team' && <>
          <div className="flex justify-between items-center mb-6" data-id="element-1567">
            <h3 className="text-lg font-semibold" data-id="element-1568">
              Team Operations & Workload
            </h3>
            <select className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-1569">
              <option data-id="element-1570">Last 30 Days</option>
              <option data-id="element-1571">This Quarter</option>
              <option data-id="element-1572">Year to Date</option>
            </select>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-1573">
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1574">
              <div className="flex items-center justify-between mb-3" data-id="element-1575">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500" data-id="element-1576">
                  <Clock className="w-5 h-5" data-id="element-1577" />
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-green-500 bg-green-500/10 px-2 py-1 rounded" data-id="element-1578">
                  <TrendingDown className="w-3 h-3" data-id="element-1579" /> 15%
                </span>
              </div>
              <p className="text-sm text-textMuted mb-1" data-id="element-1580">
                Avg Approval Turnaround
              </p>
              <h4 className="text-2xl font-bold" data-id="element-1581">1.2 days</h4>
            </div>
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1582">
              <div className="flex items-center justify-between mb-3" data-id="element-1583">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-500" data-id="element-1584">
                  <CheckCircle2 className="w-5 h-5" data-id="element-1585" />
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-red-500 bg-red-500/10 px-2 py-1 rounded" data-id="element-1586">
                  <TrendingUp className="w-3 h-3" data-id="element-1587" /> 8%
                </span>
              </div>
              <p className="text-sm text-textMuted mb-1" data-id="element-1588">Contract Turnaround</p>
              <h4 className="text-2xl font-bold" data-id="element-1589">3.4 days</h4>
            </div>
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1590">
              <div className="flex items-center justify-between mb-3" data-id="element-1591">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500" data-id="element-1592">
                  <DollarSign className="w-5 h-5" data-id="element-1593" />
                </div>
                <span className="flex items-center gap-1 text-xs font-medium text-green-500 bg-green-500/10 px-2 py-1 rounded" data-id="element-1594">
                  <TrendingDown className="w-3 h-3" data-id="element-1595" /> 12%
                </span>
              </div>
              <p className="text-sm text-textMuted mb-1" data-id="element-1596">
                Deposit Collection Speed
              </p>
              <h4 className="text-2xl font-bold" data-id="element-1597">5.1 days</h4>
            </div>
            <div className="bg-surface border border-border rounded-xl p-5" data-id="element-1598">
              <div className="flex items-center justify-between mb-3" data-id="element-1599">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary" data-id="element-1600">
                  <Users className="w-5 h-5" data-id="element-1601" />
                </div>
              </div>
              <p className="text-sm text-textMuted mb-1" data-id="element-1602">Active Assignees</p>
              <h4 className="text-2xl font-bold" data-id="element-1603">4</h4>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1604">
            {/* Turnaround Chart */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1605">
              <h3 className="text-base font-semibold mb-6" data-id="element-1606">
                Approval Turnaround by Week
              </h3>
              <div className="h-64 flex items-end justify-between gap-2" data-id="element-1607">
                {[{
              week: 'W1',
              value: 2.1
            }, {
              week: 'W2',
              value: 1.8
            }, {
              week: 'W3',
              value: 1.9
            }, {
              week: 'W4',
              value: 1.5
            }, {
              week: 'W5',
              value: 1.2
            }, {
              week: 'W6',
              value: 1.3
            }, {
              week: 'W7',
              value: 1.1
            }, {
              week: 'W8',
              value: 1.2
            }].map((data, i) => <div key={i} className="flex-1 flex flex-col items-center gap-2 group" data-id="element-1608">
                    <div className="w-full relative flex items-end justify-center h-full" data-id="element-1609">
                      <div className="w-full max-w-[32px] bg-primary/20 rounded-t-sm group-hover:bg-primary/40 transition-colors relative" style={{
                  height: `${data.value / 2.5 * 100}%`
                }} data-id="element-1610">
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-surface border border-border px-2 py-1 rounded text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10" data-id="element-1611">
                          {data.value} days
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-textMuted" data-id="element-1612">{data.week}</span>
                  </div>)}
              </div>
            </div>

            {/* Contract Processing */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-1613">
              <h3 className="text-base font-semibold mb-6" data-id="element-1614">
                Contract Processing Timeline
              </h3>
              <div className="space-y-6" data-id="element-1615">
                <div className="relative" data-id="element-1616">
                  <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" data-id="element-1617"></div>
                  <div className="flex items-start gap-4" data-id="element-1618">
                    <div className="w-6 h-6 rounded-full bg-surface border-2 border-primary flex items-center justify-center shrink-0 z-10" data-id="element-1619">
                      <div className="w-2 h-2 rounded-full bg-primary" data-id="element-1620"></div>
                    </div>
                    <div className="flex-1" data-id="element-1621">
                      <div className="flex items-center justify-between mb-1" data-id="element-1622">
                        <span className="font-medium text-sm" data-id="element-1623">
                          Drafting & Review
                        </span>
                        <span className="text-xs text-textMuted" data-id="element-1624">
                          Avg: 1.1 days
                        </span>
                      </div>
                      <div className="w-full bg-background rounded-full h-1.5 mb-1" data-id="element-1625">
                        <div className="bg-primary h-1.5 rounded-full" style={{
                      width: '30%'
                    }} data-id="element-1626"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-1627">
                  <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" data-id="element-1628"></div>
                  <div className="flex items-start gap-4" data-id="element-1629">
                    <div className="w-6 h-6 rounded-full bg-surface border-2 border-blue-500 flex items-center justify-center shrink-0 z-10" data-id="element-1630">
                      <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1631"></div>
                    </div>
                    <div className="flex-1" data-id="element-1632">
                      <div className="flex items-center justify-between mb-1" data-id="element-1633">
                        <span className="font-medium text-sm" data-id="element-1634">
                          Venue Signature
                        </span>
                        <span className="text-xs text-textMuted" data-id="element-1635">
                          Avg: 1.8 days
                        </span>
                      </div>
                      <div className="w-full bg-background rounded-full h-1.5 mb-1" data-id="element-1636">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{
                      width: '50%'
                    }} data-id="element-1637"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-1638">
                  <div className="flex items-start gap-4" data-id="element-1639">
                    <div className="w-6 h-6 rounded-full bg-surface border-2 border-green-500 flex items-center justify-center shrink-0 z-10" data-id="element-1640">
                      <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1641"></div>
                    </div>
                    <div className="flex-1" data-id="element-1642">
                      <div className="flex items-center justify-between mb-1" data-id="element-1643">
                        <span className="font-medium text-sm" data-id="element-1644">
                          Counter-Signature
                        </span>
                        <span className="text-xs text-textMuted" data-id="element-1645">
                          Avg: 0.5 days
                        </span>
                      </div>
                      <div className="w-full bg-background rounded-full h-1.5 mb-1" data-id="element-1646">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{
                      width: '15%'
                    }} data-id="element-1647"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assignee Workload Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-1648">
            <div className="p-6 border-b border-border" data-id="element-1649">
              <h3 className="text-base font-semibold" data-id="element-1650">Assignee Workload</h3>
            </div>
            <div className="overflow-x-auto" data-id="element-1651">
              <table className="w-full text-sm text-left" data-id="element-1652">
                <thead className="text-xs text-textMuted uppercase bg-background/50 border-b border-border" data-id="element-1653">
                  <tr data-id="element-1654">
                    <th className="px-6 py-4 font-medium" data-id="element-1655">Name</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1656">Assigned Deals</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1657">Inbox Items</th>
                    <th className="px-6 py-4 font-medium" data-id="element-1658">
                      Approvals (This Week)
                    </th>
                    <th className="px-6 py-4 font-medium" data-id="element-1659">Avg Response Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border" data-id="element-1660">
                  {[{
                name: 'Mark Johnson',
                deals: 12,
                inbox: 24,
                approvals: 8,
                response: '2.4 hrs',
                avatar: 'M'
              }, {
                name: 'Sarah Williams',
                deals: 8,
                inbox: 15,
                approvals: 4,
                response: '1.8 hrs',
                avatar: 'S'
              }, {
                name: 'David Chen',
                deals: 15,
                inbox: 32,
                approvals: 12,
                response: '3.1 hrs',
                avatar: 'D'
              }, {
                name: 'Emma Davis',
                deals: 5,
                inbox: 8,
                approvals: 2,
                response: '1.2 hrs',
                avatar: 'E'
              }].map((user, i) => <tr key={i} className="hover:bg-surfaceHover/50 transition-colors" data-id="element-1661">
                      <td className="px-6 py-4" data-id="element-1662">
                        <div className="flex items-center gap-3" data-id="element-1663">
                          <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-xs" data-id="element-1664">
                            {user.avatar}
                          </div>
                          <span className="font-medium" data-id="element-1665">{user.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1666">
                        <div className="flex items-center gap-2" data-id="element-1667">
                          <span className="font-medium" data-id="element-1668">{user.deals}</span>
                          <div className="w-16 h-1.5 bg-background rounded-full overflow-hidden" data-id="element-1669">
                            <div className="h-full bg-blue-500" style={{
                        width: `${user.deals / 20 * 100}%`
                      }} data-id="element-1670"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1671">
                        <div className="flex items-center gap-2" data-id="element-1672">
                          <span className="font-medium" data-id="element-1673">{user.inbox}</span>
                          {user.inbox > 20 && <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-1674"></span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-1675">
                        {user.approvals}
                      </td>
                      <td className="px-6 py-4 text-textMuted" data-id="element-1676">
                        {user.response}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </motion.div>;
}